import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, Building2, UserCheck, Lock, FileText, ShieldCheck, Users, ArrowRight, Check, Calendar, ClipboardList, UploadCloud } from "lucide-react";
import { features, steps, plans, MOUNTAIN_BG } from "../mock";
import BookDemoModal from "../components/BookDemoModal";
import ThemeToggle from "../components/ThemeToggle";

const iconMap = { FileText, ShieldCheck, Users };
const accentStyles = {
  blue: { iconBg: "bg-blue-50", icon: "text-blue-600" },
  emerald: { iconBg: "bg-emerald-50", icon: "text-emerald-600" },
  violet: { iconBg: "bg-violet-50", icon: "text-violet-600" },
};

const Landing = () => {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2f2b8f]">
              <Radar className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <span className="text-lg font-bold tracking-tight text-[#1a1a2e]">Files on Radar</span>
          </div>
          <nav className="hidden items-center gap-8 md:flex">
            <button onClick={() => scrollTo("how")} className="text-sm font-medium text-slate-600 transition-colors hover:text-[#2f2b8f]">How it works</button>
            <button onClick={() => scrollTo("pricing")} className="text-sm font-medium text-slate-600 transition-colors hover:text-[#2f2b8f]">Pricing</button>
            <button onClick={() => setDemoOpen(true)} className="text-sm font-medium text-slate-600 transition-colors hover:text-[#2f2b8f]">Book a Demo</button>
          </nav>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button onClick={() => navigate("/auth/admin")} className="rounded-xl bg-[#2f2b8f] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#26236f]">
              Sign in
            </button>
          </div>
        </div>
      </header>

      {/* Hero with subtle mountain bg */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <img src={MOUNTAIN_BG} alt="" className="h-full w-full object-cover opacity-[0.10]" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/70 to-white" />
        </div>
        <div className="relative mx-auto max-w-5xl px-6 pt-20 pb-6 text-center">
          <div className="animate-fade-up inline-flex items-center gap-2 rounded-full bg-indigo-50 px-4 py-1.5 text-sm font-medium text-indigo-800">
            <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-indigo-600" />
            The operating system for modern professional firms
          </div>
          <h1 className="animate-fade-up mx-auto mt-7 max-w-4xl text-5xl font-extrabold leading-[1.1] tracking-tight text-[#1a1a2e] md:text-6xl" style={{ animationDelay: "0.08s" }}>
            Compliance-grade document exchange, <span className="text-[#4f46e5]">on your radar.</span>
          </h1>
          <p className="animate-fade-up mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-500" style={{ animationDelay: "0.16s" }}>
            A secure portal connecting professional firms and their clients for structured document collection, review, and compliance management.
          </p>
          <div className="animate-fade-up mt-8 flex flex-wrap items-center justify-center gap-3" style={{ animationDelay: "0.22s" }}>
            <button onClick={() => navigate("/auth/admin")} className="group inline-flex items-center gap-2 rounded-xl bg-[#2f2b8f] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#26236f]">
              Start free <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => setDemoOpen(true)} className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-6 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50">
              <Calendar className="h-4 w-4" /> Book a Demo
            </button>
          </div>
        </div>
      </section>

      {/* Portals */}
      <section className="relative mx-auto max-w-6xl px-6 pt-8 pb-24">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Firm Workspace */}
          <div className="animate-fade-up rounded-2xl border border-indigo-100 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50">
              <Building2 className="h-7 w-7 text-indigo-700" strokeWidth={1.8} />
            </div>
            <h3 className="text-xl font-bold text-[#1a1a2e]">Firm Workspace</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">Manage clients, requests, documents and workflows in one place.</p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button onClick={() => navigate("/auth/admin")} className="group inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#2f2b8f] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#26236f]">
                <Lock className="h-4 w-4" /> Admin Console
              </button>
              <button onClick={() => navigate("/auth/employee")} className="group inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-indigo-300 px-5 py-3 text-sm font-semibold text-indigo-700 transition-colors hover:bg-indigo-50">
                <ClipboardList className="h-4 w-4" /> Request Center
              </button>
            </div>
          </div>

          {/* Client Workspace */}
          <div className="animate-fade-up rounded-2xl border border-emerald-100 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg" style={{ animationDelay: "0.1s" }}>
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50">
              <UploadCloud className="h-7 w-7 text-emerald-600" strokeWidth={1.8} />
            </div>
            <h3 className="text-xl font-bold text-[#1a1a2e]">Client Workspace</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-500">A secure space for clients to upload documents and respond to requests.</p>
            <div className="mt-6">
              <button onClick={() => navigate("/auth/client")} className="group inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-300 px-5 py-3 text-sm font-semibold text-emerald-700 transition-colors hover:bg-emerald-50">
                <UploadCloud className="h-4 w-4" /> Client Login <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-slate-100 bg-slate-50/50">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="grid gap-12 md:grid-cols-3">
            {features.map((f) => {
              const Icon = iconMap[f.icon];
              const a = accentStyles[f.accent];
              return (
                <div key={f.title} className="flex flex-col items-center text-center">
                  <div className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${a.iconBg}`}>
                    <Icon className={`h-7 w-7 ${a.icon}`} strokeWidth={1.8} />
                  </div>
                  <h3 className="text-lg font-bold text-[#1a1a2e]">{f.title}</h3>
                  <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-500">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="mx-auto max-w-6xl px-6 py-20">
        <h2 className="text-center text-3xl font-bold text-[#1a1a2e]">How it works</h2>
        <div className="mt-14 grid gap-10 md:grid-cols-4">
          {steps.map((s) => (
            <div key={s.n} className="flex flex-col items-center text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2f2b8f] text-base font-bold text-white shadow-md">{s.n}</div>
              <h4 className="mt-5 text-base font-bold text-[#1a1a2e]">{s.title}</h4>
              <p className="mt-2 max-w-[220px] text-sm leading-relaxed text-slate-500">{s.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-t border-slate-100 bg-slate-50/50">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-[#1a1a2e]">Simple, transparent pricing</h2>
            <p className="mt-3 text-slate-500">Start free with up to 5 clients and 10 requests per client. Upgrade anytime.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {plans.map((p) => (
              <div key={p.id} className={`relative flex flex-col rounded-2xl border bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${p.highlight ? "border-[#2f2b8f] ring-1 ring-[#2f2b8f]" : "border-slate-200"}`}>
                {p.highlight && <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#2f2b8f] px-3 py-1 text-xs font-semibold text-white">Most popular</span>}
                {p.save && <span className="absolute -top-3 right-4 rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-white">Save ~Rs.{p.save}/mo</span>}
                <h3 className="text-lg font-bold text-[#1a1a2e]">{p.name}</h3>
                <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>
                <div className="mt-5 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-[#1a1a2e]">Rs.{p.price}</span>
                  <span className="text-sm text-slate-400">{p.unit}</span>
                </div>
                {p.monthly && <p className="mt-1 text-xs font-medium text-emerald-600">= Rs.{p.monthly}/month</p>}
                <ul className="mt-6 flex-1 space-y-3">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2 text-sm text-slate-600">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" /> {perk}
                    </li>
                  ))}
                </ul>
                <button onClick={() => navigate("/auth/admin")} className={`mt-8 w-full rounded-xl px-5 py-3 text-sm font-semibold transition-colors ${p.highlight ? "bg-[#2f2b8f] text-white hover:bg-[#26236f]" : "border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-6 py-8 text-sm text-slate-400 sm:flex-row">
          <div className="flex items-center gap-2">
            <Radar className="h-4 w-4 text-[#2f2b8f]" />
            <span>© 2026 Files on Radar — Document Management Portal for Professional Firms</span>
          </div>
          <button onClick={() => setDemoOpen(true)} className="font-medium text-[#2f2b8f] hover:underline">Book a Demo</button>
        </div>
      </footer>

      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
};

export default Landing;
